/* eslint-disable max-lines */
import { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { Merge } from 'ts-essentials';

import { FiscalPrintEnabledQuery } from '@dreryk/egabinet-invoice-frontend-api';
import {
   MedicalServiceWithPractitioners as PractitionerMedicalService,
   MedicalServiceWithPractitionersQuery as PractitionerMedicalServiceQuery,
   TaxRate,
} from '@dreryk/egabinet-private-medical-service-frontend-api';

import { useCqrsFind, useCqrsGet } from '@dreryk/command-query-react-common';
import { FormContextValues, useFormController } from '@dreryk/react-common';
import {
   FormController,
   Message,
   MessageType,
   Notification,
   NotificationSize,
   NotificationType,
   StyledFlex,
} from '@dreryk/react-components';
import { Slot } from '@dreryk/watermelon';

import { protectFeature } from '~libs.shared.system';

import { PageLoader } from '../../../../shared/components/PageLoader';
import { practitionerMedicalServiceTranslationKeys } from '../../../../translations';
import { MedicalServiceDefaultPriceField } from '../../../shared/defaultPrice/MedicalServiceDefaultPriceField';
import { MedicalServiceFixedValueField } from '../../../shared/fixedValue/MedicalServiceFixedValueField';
import { MedicalServiceFormBean } from '../../../shared/form/medicalServiceForm.types';
import { MedicalServiceNameForInvoicingField } from '../../../shared/nameForInvoicing/MedicalServiceNameForInvoicingField';
import { MedicalServiceNameForInvoicingTextView } from '../../../shared/nameForInvoicing/MedicalServiceNameForInvoicingTextView';
import { nameForInvoicingPartVisible } from '../../../shared/nameForInvoicing/nameForInvocing.utils';
import { MedicalServiceNameForReceiptTextView } from '../../../shared/nameForReceipt/MedicalServiceNameForReceiptTextView';
import { MedicalServiceNameForReceiptField } from '../../../shared/nameForReceipt/fields/MedicalServiceNameForReceiptField';
import { getNameForReceipt, nameForReceiptPartVisible } from '../../../shared/nameForReceipt/nameForReceipt.utils';
import { mapPractitionerMedicalServiceToMedicalServicePractitionerBean } from '../../../shared/practitioner/practitioner.utils';
import { MedicalServicePractitionerField } from '../../../shared/practitioner/single/MedicalServicePractitionerField';
import { MedicalServiceProportionalValueField } from '../../../shared/proportionalValue/MedicalServiceProportionalValueField';
import { useCqrsSearchTaxRate } from '../../../shared/queries/useCqrsSearchTaxRate';
import { MedicalServiceNameField } from '../../../shared/serviceName/MedicalServiceNameField';
import { MedicalServiceSettlementTypeField } from '../../../shared/settlement/MedicalServiceSettlementTypeField';
import { MedicalServiceSettlementTypeVisitor } from '../../../shared/settlement/settlement.types';
import { createMedicalServiceSettlementTypeVisitor } from '../../../shared/settlement/settlement.utils';
import { MedicalServiceTaxRateField } from '../../../shared/taxRate/MedicalServiceTaxRateField';
import { getTaxRate } from '../../../shared/taxRate/taxRate.utils';
import { MedicalServiceUsingDefaultConfigurationField } from '../../../shared/usingDefaultConfiguration/MedicalServiceUsingDefaultConfigurationField';
import { MedicalServiceUsingDefaultConfiguration } from '../../../shared/usingDefaultConfiguration/usingDefaultConfiguration.types';

type MedicalServiceUpdateFormLoaderRenderProps = {
   taxRates: TaxRate[];
   medicalService: PractitionerMedicalService;
   fiscalPrintEnabled: boolean;
};

type PractitionerMedicalServiceUpdateFormProps = {
   readonly children: (renderProps: MedicalServiceUpdateFormLoaderRenderProps) => JSX.Element;
   readonly medicalServiceId: number;
   readonly practitionerId: number;
};

export const PractitionerMedicalServiceUpdateFormLoader = (
   props: PractitionerMedicalServiceUpdateFormProps
): JSX.Element => {
   const invoiceCreateAvailable = protectFeature('creatingInvoices');

   const {
      data: medicalService,
      loading: medicalServiceLoading,
      error: medicalServiceError,
   } = useCqrsGet<PractitionerMedicalService>(() => {
      const query = new PractitionerMedicalServiceQuery();
      query.id = props.medicalServiceId;
      query.practitionerId = Slot.of(props.practitionerId);
      return query;
   });
   const { data: taxRates, loading: taxRatesLoading, error: taxRatesError } = useCqrsSearchTaxRate();

   const {
      data: fiscalPrintEnabled,
      loading: fiscalPrintEnabledLoading,
      error: fiscalPrintEnabledError,
   } = useCqrsFind(() => new FiscalPrintEnabledQuery(), { enabled: invoiceCreateAvailable });

   if (medicalServiceLoading || taxRatesLoading || fiscalPrintEnabledLoading) {
      return <PageLoader />;
   }

   if (medicalServiceError || taxRatesError || fiscalPrintEnabledError) {
      return (
         <Message
            testId={'medicalServiceUpdateLoaderError'}
            type={MessageType.NEGATIVE}
            label={practitionerMedicalServiceTranslationKeys.update.loadDataError}
            displayIndicator
         />
      );
   }

   return props.children({
      medicalService: medicalService,
      taxRates: taxRates,
      fiscalPrintEnabled: Boolean(fiscalPrintEnabled),
   });
};

const StyledForColumnsSpan = styled.div`
   grid-column: 1 / span 4;
`;
StyledForColumnsSpan.displayName = 'StyledTwoColumnsSpan';

const StyledForm = styled.form`
   display: grid;
   grid-template-columns: repeat(4, 1fr);
   column-gap: 36px;
   row-gap: 24px;
`;
StyledForm.displayName = 'StyledForm';

export type PractitionerMedicalServiceUpdateFormBean = Merge<
   MedicalServiceFormBean,
   {
      id: number;
      usingDefaultConfiguration: MedicalServiceUsingDefaultConfiguration;
   }
>;

export type PractitionerMedicalServiceUpdateFormController = FormContextValues<PractitionerMedicalServiceUpdateFormBean>;

declare type PractitionerMedicalServiceUpdateFormRendererProps = Pick<
   PractitionerMedicalServiceUpdateFormController,
   'handleSubmit' | 'formState'
>;

declare type PractitionerMedicalServiceUpdateFormRenderer = (
   renderProps: PractitionerMedicalServiceUpdateFormRendererProps
) => JSX.Element;

type PractitionerMedicalServiceCreateUpdateProps = {
   readonly children: PractitionerMedicalServiceUpdateFormRenderer;
   readonly medicalService: PractitionerMedicalService;
   readonly taxRates: TaxRate[];
   readonly fiscalPrintEnabled: boolean;
};

// eslint-disable-next-line max-lines-per-function
export const PractitionerMedicalServiceUpdateForm = (
   props: PractitionerMedicalServiceCreateUpdateProps
): JSX.Element => {
   const { medicalService, taxRates } = props;
   const [selectedPractitioner] = medicalService.practitioners;
   const { practitioner } = selectedPractitioner;

   const invoiceCreateAvailable = protectFeature('creatingInvoices');

   const controller = useFormController<PractitionerMedicalServiceUpdateFormBean>(
      useMemo(() => {
         const settlementTypeFormPart = createMedicalServiceSettlementTypeVisitor(
            selectedPractitioner.settlementConfiguration?.settlementType ??
               medicalService.defaultSettlementConfiguration.settlementType
         )<Pick<PractitionerMedicalServiceUpdateFormBean, 'settlementType' | 'fixedValue' | 'proportionalValue'>>({
            visitNone: () => ({
               settlementType: 'None',
               fixedValue: undefined,
               proportionalValue: undefined,
            }),
            visitFixed: () => ({
               settlementType: 'Fixed',
               fixedValue:
                  selectedPractitioner.settlementConfiguration?.fixedValue?.asNumber() ??
                  medicalService.defaultSettlementConfiguration.fixedValue!.asNumber(),
               proportionalValue: undefined,
            }),
            visitProportional: () => ({
               settlementType: 'Proportional',
               fixedValue: undefined,
               proportionalValue:
                  selectedPractitioner.settlementConfiguration?.proportionalValue ??
                  medicalService.defaultSettlementConfiguration.proportionalValue,
            }),
         });

         const formData: PractitionerMedicalServiceUpdateFormBean = {
            id: medicalService.id,
            name: medicalService.name,
            defaultPrice: selectedPractitioner.individualPrice?.asNumber() ?? medicalService.defaultPrice.asNumber(),
            taxRate: getTaxRate(taxRates, medicalService.taxRateId),
            usingDefaultConfiguration: selectedPractitioner.usingDefaultConfiguration ?? false,
            ...settlementTypeFormPart,
         };

         formData.practitioners = medicalService.practitioners.map(
            mapPractitionerMedicalServiceToMedicalServicePractitionerBean(formData)
         );

         return formData;
         // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [medicalService, taxRates])
   );

   const { settlementType, defaultPrice, usingDefaultConfiguration } = controller.watch([
      'settlementType',
      'defaultPrice',
      'usingDefaultConfiguration',
   ]);

   const medicalServiceSettlementTypeVisitor = useMemo<MedicalServiceSettlementTypeVisitor>(
      () => createMedicalServiceSettlementTypeVisitor(settlementType),
      [settlementType]
   );

   const useDefaultConfiguration = useCallback(
      (checked: boolean) => {
         if (checked) {
            controller.setValue('defaultPrice', medicalService.defaultPrice.asNumber());
            controller.trigger('defaultPrice');

            createMedicalServiceSettlementTypeVisitor(medicalService.defaultSettlementConfiguration.settlementType)({
               visitNone: () => {
                  controller.setValue('settlementType', 'None');
                  controller.trigger('settlementType');
               },
               visitFixed: () => {
                  controller.setValue('settlementType', 'Fixed');
                  controller.trigger('settlementType');
                  controller.setValue(
                     'fixedValue',
                     medicalService.defaultSettlementConfiguration.fixedValue?.asNumber()
                  );
                  controller.trigger('fixedValue');
               },
               visitProportional: () => {
                  controller.setValue('settlementType', 'Proportional');
                  controller.trigger('settlementType');
                  controller.setValue(
                     'proportionalValue',
                     medicalService.defaultSettlementConfiguration.proportionalValue
                  );
                  controller.trigger('proportionalValue');
               },
            });
         }
      },
      [medicalService, controller]
   );

   return (
      <>
         <FormController controller={controller.register('id')} />
         <StyledForm noValidate autoComplete={'off'}>
            <StyledForColumnsSpan>
               <StyledFlex gap={'4px'} direction={'column'}>
                  <MedicalServiceNameField name={'name'} register={controller.register} disabled />
                  <Notification
                     testId={'editNotification'}
                     type={NotificationType.INFO}
                     label={practitionerMedicalServiceTranslationKeys.update.notification}
                     size={NotificationSize.SMALLER}
                  />
               </StyledFlex>
            </StyledForColumnsSpan>
            {nameForInvoicingPartVisible(controller, invoiceCreateAvailable) && (
               <StyledForColumnsSpan>
                  <MedicalServiceNameForInvoicingTextView watch={controller.watch} disabled>
                     <MedicalServiceNameForInvoicingField
                        name={'nameForInvoicing'}
                        defaultValue={props.medicalService.nameForInvoicing ?? props.medicalService.name}
                        register={controller.register}
                        setValue={controller.setValue}
                        trigger={controller.trigger}
                        watch={controller.watch}
                        disabled
                     />
                  </MedicalServiceNameForInvoicingTextView>
               </StyledForColumnsSpan>
            )}

            {nameForReceiptPartVisible(controller, props.fiscalPrintEnabled) && (
               <StyledForColumnsSpan>
                  <MedicalServiceNameForReceiptTextView watch={controller.watch} disabled>
                     <MedicalServiceNameForReceiptField
                        defaultValue={getNameForReceipt(
                           props.medicalService.nameForReceipt,
                           props.medicalService.name,
                           props.medicalService.nameForInvoicing
                        )}
                        name={'nameForReceipt'}
                        register={controller.register}
                        setValue={controller.setValue}
                        watch={controller.watch}
                        disabled
                     />
                  </MedicalServiceNameForReceiptTextView>
               </StyledForColumnsSpan>
            )}
            <StyledForColumnsSpan>
               <MedicalServiceUsingDefaultConfigurationField
                  name={'usingDefaultConfiguration'}
                  onSelectionChange={useDefaultConfiguration}
                  register={controller.register}
               />
            </StyledForColumnsSpan>

            <MedicalServiceDefaultPriceField
               name={'defaultPrice'}
               register={controller.register}
               required={!usingDefaultConfiguration}
               disabled={usingDefaultConfiguration}
            />
            <MedicalServiceTaxRateField name={'taxRate'} register={controller.register} taxRates={taxRates} disabled />

            <MedicalServiceSettlementTypeField
               name={'settlementType'}
               register={controller.register}
               required={!usingDefaultConfiguration}
               disabled={usingDefaultConfiguration}
            />
            {medicalServiceSettlementTypeVisitor({
               visitNone: () => null,

               visitFixed: () => (
                  <MedicalServiceFixedValueField
                     name={'fixedValue'}
                     register={controller.register}
                     defaultPrice={defaultPrice}
                     trigger={controller.trigger}
                     required={!usingDefaultConfiguration}
                     disabled={usingDefaultConfiguration}
                  />
               ),

               visitProportional: () => (
                  <MedicalServiceProportionalValueField
                     name={'proportionalValue'}
                     register={controller.register}
                     required={!usingDefaultConfiguration}
                     disabled={usingDefaultConfiguration}
                  />
               ),
            })}

            <StyledForColumnsSpan>
               <MedicalServicePractitionerField
                  register={controller.register}
                  control={controller.control}
                  practitioners={[practitioner]}
               />
            </StyledForColumnsSpan>
         </StyledForm>

         {props.children({
            handleSubmit: controller.handleSubmit,
            formState: controller.formState,
         })}
      </>
   );
};
